/**
 * e-Invoice List Page — LHDN MyInvois-ready invoice management.
 * Shows list, filters, status badges, and triggers create/edit/submit/cancel.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Plus, Send, XCircle, Edit2, Trash2, RefreshCw, Search,
  CheckCircle2, AlertTriangle, Clock, FileSignature, ExternalLink, DollarSign,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  Draft:      { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
  Submitted:  { bg: 'bg-blue-100', text: 'text-blue-700', icon: Send },
  Validated:  { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
  Rejected:   { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  Cancelled:  { bg: 'bg-amber-100', text: 'text-amber-700', icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_STYLE[status] || STATUS_STYLE.Draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const EInvoicePage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [myinvoisHealth, setMyinvoisHealth] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('document_type', filterType);
      if (search) params.append('search', search);
      const [list, summary, ref] = await Promise.all([
        axios.get(`${API}/einvoice?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/einvoice/summary/stats`, { withCredentials: true }),
        axios.get(`${API}/einvoice/reference`, { withCredentials: true }),
      ]);
      setInvoices(list.data.items || []);
      setStats(summary.data);
      setMyinvoisHealth(ref.data.myinvois_health);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterStatus, filterType]);

  const handleSubmit = async (id) => {
    if (!window.confirm('Hantar invoice ini ke LHDN MyInvois? Tindakan ini tidak boleh dibatalkan.')) return;
    try {
      await axios.post(`${API}/einvoice/${id}/submit`, {}, { withCredentials: true });
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Gagal submit');
    }
  };

  const handleCancel = async (id) => {
    const reason = window.prompt('Sebab pembatalan (wajib untuk LHDN):');
    if (!reason) return;
    try {
      await axios.post(`${API}/einvoice/${id}/cancel`, { reason }, { withCredentials: true });
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Gagal cancel');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Buang invoice draft ini?')) return;
    try {
      await axios.delete(`${API}/einvoice/${id}`, { withCredentials: true });
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Gagal buang');
    }
  };

  return (
    <div className="space-y-5" data-testid="einvoice-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 rounded-2xl p-5 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileSignature className="w-7 h-7" />
              e-Invoice LHDN
            </h1>
            <p className="text-red-200 text-sm mt-1">
              Invoice digital patuh LHDN MyInvois — wajib untuk semua business Malaysia mulai 2026.
            </p>
          </div>
          <button
            onClick={() => navigate('/einvoice/new')}
            data-testid="einvoice-new-btn"
            className="bg-white text-red-700 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 flex items-center gap-2 self-start"
          >
            <Plus className="w-4 h-4" /> Invoice Baru
          </button>
        </div>

        {myinvoisHealth && (
          <div className="mt-4 flex flex-wrap items-start gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full font-semibold shrink-0 ${
              myinvoisHealth.mode === 'stub' ? 'bg-amber-400 text-amber-900' :
              myinvoisHealth.mode === 'sandbox' ? 'bg-blue-400 text-blue-900' :
              'bg-emerald-400 text-emerald-900'
            }`} data-testid="myinvois-mode-badge">
              {myinvoisHealth.mode === 'stub' ? 'MODE: STUB (demo)' :
               myinvoisHealth.mode === 'sandbox' ? 'MODE: SANDBOX LHDN' :
               'MODE: PRODUCTION LHDN'}
            </span>
            {myinvoisHealth.mode === 'stub' && (
              <span className="text-red-200 leading-tight">
                Tambah CLIENT_ID + SECRET MyInvois dalam .env untuk aktifkan submission sebenar
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="einvoice-stats">
          <div className="bg-white rounded-xl p-4 border border-gray-100 min-w-0">
            <p className="text-xs text-gray-500 truncate">Jumlah Invoice</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_invoices}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 col-span-2 md:col-span-2 lg:col-span-1 min-w-0">
            <p className="text-xs text-emerald-600 truncate">Nilai Kasar</p>
            <p className="text-xl font-bold text-emerald-700 truncate">RM {stats.grand_total_value.toLocaleString()}</p>
          </div>
          {stats.by_status.map((s) => (
            <div key={s.status} className="bg-white rounded-xl p-4 border border-gray-100 min-w-0">
              <p className="text-xs text-gray-500 truncate">{s.status}</p>
              <p className="text-xl font-bold text-gray-900">{s.count}</p>
              <p className="text-xs text-gray-400 truncate">RM {s.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchAll()}
            placeholder="Cari invoice no. atau nama pembeli…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-500"
            data-testid="einvoice-search"
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="einvoice-filter-status">
          <option value="">Semua status</option>
          {['Draft', 'Submitted', 'Validated', 'Rejected', 'Cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="einvoice-filter-type">
          <option value="">Semua jenis</option>
          {['Invoice', 'Credit Note', 'Debit Note', 'Refund Note'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={fetchAll} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-100">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Table (desktop) */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hidden md:block" data-testid="einvoice-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="p-3">No. Invoice</th>
                <th className="p-3">Jenis</th>
                <th className="p-3">Tarikh</th>
                <th className="p-3">Pembeli</th>
                <th className="p-3 text-right">Jumlah</th>
                <th className="p-3">Status</th>
                <th className="p-3">Tindakan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400">
                  {loading ? 'Memuatkan…' : 'Tiada invoice. Klik "Invoice Baru" untuk mulakan.'}
                </td></tr>
              )}
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-red-50/30" data-testid={`einvoice-row-${inv.id}`}>
                  <td className="p-3 font-medium text-gray-900">
                    <button onClick={() => navigate(`/einvoice/${inv.id}`)} className="hover:text-red-700">
                      {inv.invoice_number}
                    </button>
                    {inv.myinvois_qr_code && (
                      <a href={inv.myinvois_qr_code} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-0.5 mt-0.5">
                        <ExternalLink className="w-3 h-3" /> MyInvois
                      </a>
                    )}
                  </td>
                  <td className="p-3 text-gray-600">{inv.document_type}</td>
                  <td className="p-3 text-gray-600">{inv.issue_date}</td>
                  <td className="p-3 text-gray-700">
                    <p className="font-medium">{inv.buyer?.name || '—'}</p>
                    <p className="text-xs text-gray-400">TIN: {inv.buyer?.tin || '—'}</p>
                  </td>
                  <td className="p-3 text-right font-semibold text-gray-900">
                    RM {Number(inv.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-3"><StatusBadge status={inv.status} /></td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {(inv.status === 'Draft' || inv.status === 'Rejected') && (
                        <>
                          <button
                            onClick={() => navigate(`/einvoice/${inv.id}`)}
                            data-testid={`einvoice-edit-${inv.id}`}
                            title="Edit"
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleSubmit(inv.id)}
                            data-testid={`einvoice-submit-${inv.id}`}
                            title="Submit ke LHDN"
                            className="p-1.5 hover:bg-blue-50 rounded text-blue-600 hover:text-blue-700"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(inv.id)}
                            data-testid={`einvoice-delete-${inv.id}`}
                            title="Buang"
                            className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {inv.status === 'Validated' && (
                        <button
                          onClick={() => handleCancel(inv.id)}
                          data-testid={`einvoice-cancel-${inv.id}`}
                          title="Cancel"
                          className="p-1.5 hover:bg-amber-50 rounded text-amber-600 hover:text-amber-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card layout (mobile < md) */}
      <div className="md:hidden space-y-2" data-testid="einvoice-cards">
        {invoices.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">
            {loading ? 'Memuatkan…' : 'Tiada invoice. Klik "Invoice Baru".'}
          </div>
        )}
        {invoices.map((inv) => (
          <div key={inv.id} className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm" data-testid={`einvoice-card-${inv.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <button onClick={() => navigate(`/einvoice/${inv.id}`)} className="font-semibold text-gray-900 hover:text-red-700 text-sm block truncate text-left py-2 px-1 -my-1 -mx-1 rounded hover:bg-red-50">
                  {inv.invoice_number}
                </button>
                <p className="text-xs text-gray-500 truncate">{inv.document_type} • {inv.issue_date}</p>
              </div>
              <StatusBadge status={inv.status} />
            </div>
            <div className="flex justify-between items-end gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">Pembeli</p>
                <p className="text-sm text-gray-800 truncate">{inv.buyer?.name || '—'}</p>
              </div>
              <p className="text-base font-bold text-gray-900 shrink-0">
                RM {Number(inv.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="flex gap-1 mt-3 pt-3 border-t border-gray-50">
              {(inv.status === 'Draft' || inv.status === 'Rejected') && (
                <>
                  <button onClick={() => navigate(`/einvoice/${inv.id}`)} className="flex-1 py-2 bg-gray-50 rounded-lg text-xs font-medium flex items-center justify-center gap-1 text-gray-700">
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleSubmit(inv.id)} className="flex-1 py-2 bg-blue-50 rounded-lg text-xs font-medium flex items-center justify-center gap-1 text-blue-700">
                    <Send className="w-3.5 h-3.5" /> Submit
                  </button>
                  <button onClick={() => handleDelete(inv.id)} className="py-2 px-3 bg-red-50 rounded-lg text-xs font-medium text-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {inv.status === 'Validated' && (
                <>
                  <button onClick={() => navigate(`/einvoice/${inv.id}`)} className="flex-1 py-2 bg-gray-50 rounded-lg text-xs font-medium flex items-center justify-center gap-1 text-gray-700">
                    Lihat
                  </button>
                  {inv.myinvois_qr_code && (
                    <a href={inv.myinvois_qr_code} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 bg-emerald-50 rounded-lg text-xs font-medium text-emerald-700 flex items-center justify-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" /> MyInvois
                    </a>
                  )}
                  <button onClick={() => handleCancel(inv.id)} className="py-2 px-3 bg-amber-50 rounded-lg text-xs font-medium text-amber-700">
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EInvoicePage;
